/**
 * Tenant Service - API client for multi-tenant management
 */
import api from './api';

const TENANTS_BASE = '/tenants';

/**
 * Get all tenants
 */
export const getTenants = async (isActive = null, options = {}) => {
  const params = {
    ...(isActive !== null ? { is_active: isActive } : {}),
    ...(options.q ? { q: options.q } : {}),
    ...(options.limit ? { limit: options.limit } : {}),
    ...(options.excludePlatform ? { exclude_platform: true } : {}),
  };
  const response = await api.get(TENANTS_BASE, { params });
  return response.data;
};

/**
 * Get tenant by ID
 */
export const getTenant = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}`);
  return response.data;
};

/**
 * Get tenant by slug
 */
export const getTenantBySlug = async (slug) => {
  const response = await api.get(`${TENANTS_BASE}/slug/${slug}`);
  return response.data;
};

/**
 * Create a new tenant
 */
// confirmDuplicate=true creates a tenant even though one with the same name
// exists. The backend refuses by default (409) rather than silently
// auto-suffixing the slug, which is how one double-clicked Create button
// produced two identical tenants.
export const createTenant = async (tenantData, { confirmDuplicate = false } = {}) => {
  const url = confirmDuplicate ? `${TENANTS_BASE}?confirm_duplicate=true` : TENANTS_BASE;
  const response = await api.post(url, tenantData);
  return response.data;
};

/**
 * Update a tenant
 */
export const updateTenant = async (tenantId, tenantData) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}`, tenantData);
  return response.data;
};

/**
 * Preview what tenant deletion will remove
 */
export const getTenantDeletePreview = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/delete-preview`);
  return response.data;
};

/**
 * Delete a tenant
 */
export const deleteTenant = async (tenantId, confirmation) => {
  const response = await api.delete(`${TENANTS_BASE}/${tenantId}`, {
    params: { confirmation },
  });
  return response.data;
};

/**
 * Trigger tenant onboarding (seed defaults)
 */
export const onboardTenant = async (tenantId) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/onboard`);
  return response.data;
};

/**
 * Get available subscription tiers with limits
 */
export const getSubscriptionTiers = async () => {
  const response = await api.get(`${TENANTS_BASE}/meta/tiers`);
  return response.data;
};

/**
 * Change a tenant's subscription tier (upgrade/downgrade)
 */
export const changeTenantTier = async (tenantId, tier) => {
  const response = await api.put(`${TENANTS_BASE}/${tenantId}/subscription`, { tier });
  return response.data;
};

// ==================== Feature Entitlement Overrides ====================
// Superadmin-only grants/denials of individual features, independent of
// tier. Tier stays the default bundle; an override is a per-tenant
// exception on top of it (e.g. white-label on a Growth deal).

/**
 * List every registered feature's tier default, override, and effective value.
 */
export const getTenantEntitlements = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/entitlements`);
  return response.data;
};

/**
 * Set or clear one tenant's override for a feature.
 * value: true (force on) | false (force off) | null (clear -> inherit tier default)
 */
export const setTenantEntitlement = async (tenantId, featureKey, { value, note, expiresAt } = {}) => {
  const response = await api.put(`${TENANTS_BASE}/${tenantId}/entitlements/${featureKey}`, {
    value,
    note: note || null,
    expires_at: expiresAt || null,
  });
  return response.data;
};

// ==================== Export / Import ====================

/**
 * Export full tenant config as JSON and trigger browser download
 */
export const exportTenant = async (tenantId, tenantName) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/export`);
  const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tenant-${tenantName || tenantId}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return response.data;
};

/**
 * Import a tenant from JSON config
 */
export const importTenant = async (jsonData) => {
  const response = await api.post(`${TENANTS_BASE}/import`, jsonData);
  return response.data;
};

// ==================== Notification Contacts ====================

/**
 * Get notification contacts and tier limits
 */
export const getNotificationContacts = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/notification-contacts`);
  return response.data;
};

/**
 * Update notification contacts (emails and WhatsApp phones)
 */
export const renameWorkspace = async (tenantId, name) => {
  const response = await api.put(`${TENANTS_BASE}/${tenantId}/name`, { name });
  return response.data;
};

export const updateNotificationContacts = async (tenantId, data) => {
  const response = await api.put(`${TENANTS_BASE}/${tenantId}/notification-contacts`, data);
  return response.data;
};

/**
 * Send one sample lead alert to the configured recipients, so an admin can
 * see alerts arrive without waiting for a real lead.
 */
export const sendTestNotification = async (tenantId) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/notification-contacts/test`);
  return response.data;
};

// ==================== Tenant Details (superadmin) ====================

/**
 * Everything about one workspace: contact details, membership-aware people,
 * effective allowances with usage, and every agent including disabled ones.
 *
 * Superadmin-only and separate from the tenant list on purpose. Contact and
 * operational details do not belong in the shared tenant-list response.
 */
export const getTenantDetails = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/details`);
  return response.data;
};

// ==================== WhatsApp Inbox ====================

/**
 * Get inbound WhatsApp messages (replies to template notifications) for a tenant
 */
export const getWhatsAppInbox = async (tenantId, limit = 50, offset = 0) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/whatsapp-inbox`, {
    params: { limit, offset },
  });
  return response.data;
};

// ==================== BSP Conversations ====================

/**
 * Get BSP/WhatsApp conversations mirrored into Serviti.
 * Optional filters: provider, status, lead_id.
 */
export const getBspConversations = async (tenantId, params = {}) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/bsp-conversations`, { params });
  return response.data;
};

/**
 * Get append-only messages for one BSP conversation.
 */
export const getBspConversationMessages = async (tenantId, conversationId, params = {}) => {
  const response = await api.get(
    `${TENANTS_BASE}/${tenantId}/bsp-conversations/${conversationId}/messages`,
    { params },
  );
  return response.data;
};

// ==================== The workspace's own WhatsApp number ====================

/** Connection state, the add-on, and what the browser needs to open Meta's signup. */
export const getWhatsAppNumber = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/whatsapp`);
  return response.data;
};

/** Finish Meta's Embedded Signup: { code, waba_id, phone_number_id, business_id, onboarding }. */
export const connectWhatsAppNumber = async (tenantId, body) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/whatsapp/connect`, body);
  return response.data;
};

/**
 * Connect a Cloud API number the customer already runs in their own Meta app:
 * { phone_number_id, waba_id, access_token, app_secret? }. Used when Serviti
 * is not the Tech Provider for their account.
 */
export const connectOwnWhatsAppApp = async (tenantId, body) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/whatsapp/connect-own-app`, body);
  return response.data;
};

// ---------------------------------------------------------------- Instagram
// A workspace's own Instagram account. Only the "own app" path exists:
// Instagram messaging permissions need App Review.
export const getInstagram = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/instagram`);
  return response.data;
};

/** { ig_account_id, access_token, app_secret, page_id?, dedicated_app_confirmed } */
export const connectOwnInstagramApp = async (tenantId, body) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/instagram/connect-own-app`, body);
  return response.data;
};

/** { ai_enabled?, agent_id? } */
export const updateInstagram = async (tenantId, body) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}/instagram`, body);
  return response.data;
};

export const disconnectInstagram = async (tenantId) => {
  const response = await api.delete(`${TENANTS_BASE}/${tenantId}/instagram`);
  return response.data;
};

/** { ai_enabled?, agent_id? } */
export const updateWhatsAppNumber = async (tenantId, body) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}/whatsapp`, body);
  return response.data;
};

export const disconnectWhatsAppNumber = async (tenantId) => {
  const response = await api.delete(`${TENANTS_BASE}/${tenantId}/whatsapp`);
  return response.data;
};

/** handler: 'team' takes the chat over from the AI; 'ai' hands it back. */
export const setWhatsAppChatHandler = async (tenantId, conversationId, handler) => {
  const response = await api.post(
    `${TENANTS_BASE}/${tenantId}/whatsapp/conversations/${conversationId}/handler`,
    { handler },
  );
  return response.data;
};

export const replyToWhatsAppChat = async (tenantId, conversationId, text) => {
  const response = await api.post(
    `${TENANTS_BASE}/${tenantId}/whatsapp/conversations/${conversationId}/reply`,
    { text },
  );
  return response.data;
};

// ==================== Prompts ====================

/**
 * Get all prompts for a tenant
 */
export const getPrompts = async (tenantId, category = null) => {
  const params = category ? { category } : {};
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/prompts`, { params });
  return response.data;
};

/**
 * Get prompt by ID
 */
export const getPrompt = async (tenantId, promptId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/prompts/${promptId}`);
  return response.data;
};

/**
 * Create a prompt
 */
export const createPrompt = async (tenantId, promptData) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/prompts`, promptData);
  return response.data;
};

/**
 * Update a prompt
 */
export const updatePrompt = async (tenantId, promptId, promptData) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}/prompts/${promptId}`, promptData);
  return response.data;
};

/**
 * Delete a prompt
 */
export const deletePrompt = async (tenantId, promptId) => {
  await api.delete(`${TENANTS_BASE}/${tenantId}/prompts/${promptId}`);
};

// ==================== Agents ====================

/**
 * Get all agents for a tenant
 */
export const getAgents = async (tenantId, agentType = null) => {
  const params = agentType ? { agent_type: agentType } : {};
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/agents`, { params });
  return response.data;
};

/**
 * Get default agent for a tenant
 */
export const getDefaultAgent = async (tenantId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/agents/default`);
  return response.data;
};

/**
 * Get agent by ID
 */
export const getAgent = async (tenantId, agentId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/agents/${agentId}`);
  return response.data;
};

/**
 * Create an agent
 */
export const createAgent = async (tenantId, agentData) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/agents`, agentData);
  return response.data;
};

/**
 * Update an agent
 */
export const updateAgent = async (tenantId, agentId, agentData) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}/agents/${agentId}`, agentData);
  return response.data;
};

/**
 * Delete an agent
 */
export const deleteAgent = async (tenantId, agentId) => {
  await api.delete(`${TENANTS_BASE}/${tenantId}/agents/${agentId}`);
};

// ==================== Integrations ====================

/**
 * Get all integrations for a tenant
 */
export const getIntegrations = async (tenantId, integrationType = null) => {
  const params = integrationType ? { integration_type: integrationType } : {};
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/integrations`, { params });
  return response.data;
};

/**
 * Get integration by ID
 */
export const getIntegration = async (tenantId, integrationId) => {
  const response = await api.get(`${TENANTS_BASE}/${tenantId}/integrations/${integrationId}`);
  return response.data;
};

/**
 * Create an integration
 */
export const createIntegration = async (tenantId, integrationData) => {
  const response = await api.post(`${TENANTS_BASE}/${tenantId}/integrations`, integrationData);
  return response.data;
};

/**
 * Update an integration
 */
export const updateIntegration = async (tenantId, integrationId, integrationData) => {
  const response = await api.patch(`${TENANTS_BASE}/${tenantId}/integrations/${integrationId}`, integrationData);
  return response.data;
};

/**
 * Delete an integration
 */
export const deleteIntegration = async (tenantId, integrationId) => {
  await api.delete(`${TENANTS_BASE}/${tenantId}/integrations/${integrationId}`);
};

/**
 * Test WATI outbound credentials. Backend hits GET /api/v1/getContacts on
 * WATI and returns a structured diagnostic. Payload is optional — omit both
 * fields to test against the stored integration row.
 */
export const testWatiIntegration = async (tenantId, payload = {}) => {
  const response = await api.post(
    `${TENANTS_BASE}/${tenantId}/integrations/wati/test`,
    payload
  );
  return response.data;
};

export default {
  // Tenants
  getTenants,
  getTenant,
  getTenantBySlug,
  createTenant,
  updateTenant,
  deleteTenant,
  onboardTenant,
  getSubscriptionTiers,
  changeTenantTier,
  exportTenant,
  importTenant,

  // Prompts
  getPrompts,
  getPrompt,
  createPrompt,
  updatePrompt,
  deletePrompt,
  
  // Agents
  getAgents,
  getDefaultAgent,
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  
  // Integrations
  getIntegrations,
  getIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testWatiIntegration,
};
